// src/components/BulkNotificationDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import { createBulkNotifications, BulkNotificationPayload } from '../services/notificationService';

interface BulkNotificationDialogProps {
  open: boolean;
  onClose: () => void;
  egliseId: number;
  onSuccess: () => void;
}

const BulkNotificationDialog: React.FC<BulkNotificationDialogProps> = ({
  open,
  onClose,
  egliseId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { error: showError } = useNotification();
  const [type, setType] = useState<'push' | 'sms' | 'email' | 'in_app'>('push');
  const [message, setMessage] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      showError(t('dialogs.bulkNotification.messageRequired'));
      return;
    }

    setLoading(true);

    try {
      const payload: BulkNotificationPayload = {
        eglise_id: egliseId,
        type,
        message: message.trim(),
      };

      if (scheduledAt) {
        const scheduledDate = new Date(scheduledAt);
        if (scheduledDate < new Date()) {
          showError(t('validation.datePast'));
          setLoading(false);
          return;
        }
        payload.scheduled_at = scheduledDate.toISOString();
      }

      await createBulkNotifications(payload);
      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error('Failed to create bulk notifications:', err);
      showError(err.response?.data?.detail || t('admin.notifications.errorCreating'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setType('push');
      setMessage('');
      setScheduledAt('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.notifications.createBulk')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('dialogs.bulkNotification.description')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>{t('dialogs.bulkNotification.typeLabel')}</InputLabel>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value as any)}
              label={t('dialogs.bulkNotification.typeLabel')}
              disabled={loading}
            >
              <MenuItem value="push">{t('admin.notifications.types.push')}</MenuItem>
              <MenuItem value="sms">{t('admin.notifications.types.sms')}</MenuItem>
              <MenuItem value="email">{t('admin.notifications.types.email')}</MenuItem>
              <MenuItem value="in_app">{t('admin.notifications.types.in_app')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={t('dialogs.bulkNotification.messageLabel')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={4}
            fullWidth
            required
            disabled={loading}
            placeholder={t('dialogs.bulkNotification.messagePlaceholder')}
          />

          <TextField
            label={t('dialogs.bulkNotification.scheduleLabel')}
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            fullWidth
            disabled={loading}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={t('dialogs.bulkNotification.scheduleHelper')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !message.trim()}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkNotificationDialog;
